/**
 * PrepareSlide — Feature 1 "Prepare" wizard step (real mode only).
 *
 * Runs the dataset-prep action (resize + caption + generate the hidden trigger
 * label) before training. Mirrors ReferencesSlide's two-column layout:
 *   - Left: subject-name field, "Prepare dataset" button, trigger-label chip.
 *   - Right: progress while running, then a review grid of each image with its
 *     editable caption.
 */

import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type {
  LoraDatasetCaption,
  LoraPrepRun,
  LoraPrepStatusSlug,
} from "@/types/loras";

/** Extended caption with previewUrl from the asset URL map (injected by LoraWizard). */
interface CaptionWithUrl extends LoraDatasetCaption {
  previewUrl: string | null;
}

interface PrepareSlideProps {
  subjectName: string;
  onCommitSubjectName: (name: string) => void;
  isUpdatingSubjectName?: boolean;
  triggerWord: string | null;
  prepRun: LoraPrepRun | null;
  captions: CaptionWithUrl[];
  imageCount: number;
  minImages: number;
  loading?: boolean;
  onStartPrep: () => void;
  isStarting?: boolean;
  onUpdateCaption: (assetId: string, caption: string) => void;
}

const RUNNING: ReadonlySet<LoraPrepStatusSlug> = new Set([
  "queued",
  "processing",
  "captioning",
  "packaging",
]);

const STATUS_LABEL: Record<string, string> = {
  queued: "Queued…",
  processing: "Resizing images…",
  captioning: "Captioning images…",
  packaging: "Packaging…",
  prepared: "Prepared",
  failed: "Failed",
};

export function PrepareSlide({
  subjectName,
  onCommitSubjectName,
  isUpdatingSubjectName,
  triggerWord,
  prepRun,
  captions,
  imageCount,
  minImages,
  loading,
  onStartPrep,
  isStarting,
  onUpdateCaption,
}: PrepareSlideProps) {
  const status = prepRun?.statusSlug;
  const isRunning =
    Boolean(status && RUNNING.has(status)) || Boolean(isStarting);
  const isPrepared = status === "prepared";
  const isFailed = status === "failed";
  const enoughImages = imageCount >= minImages;

  const [nameDraft, setNameDraft] = useState(subjectName);
  useEffect(() => setNameDraft(subjectName), [subjectName]);

  return (
    <div className="flex gap-8">
      {/* Left column */}
      <div className="w-80 flex-shrink-0 space-y-5">
        <div>
          <h2 className="text-lg font-medium text-foreground">
            Prepare dataset
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            We resize your reference images and write a one-line caption for
            each so the model learns your subject. This runs before training.
          </p>
        </div>

        <div className="space-y-1.5">
          <label className="text-xs uppercase tracking-wider text-muted-foreground">
            Subject name
          </label>
          <Input
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            onBlur={() => {
              if (nameDraft.trim() && nameDraft !== subjectName) {
                onCommitSubjectName(nameDraft.trim());
              }
            }}
            disabled={Boolean(isUpdatingSubjectName)}
            placeholder="e.g. Murphy"
            className="bg-white/5 border-white/10"
          />
          <p className="text-[11px] text-muted-foreground">
            How you&apos;ll refer to this subject when prompting (e.g. type
            &quot;@&quot; and pick the name). You never type the hidden token
            below.
          </p>
        </div>

        <div className="space-y-1.5">
          <label className="text-xs uppercase tracking-wider text-muted-foreground">
            Hidden trigger token
          </label>
          {triggerWord ? (
            <div className="flex items-center gap-2">
              <code className="rounded bg-white/10 px-2 py-1 font-mono text-sm text-foreground">
                {triggerWord}
              </code>
              <span className="text-[11px] text-muted-foreground">
                generated for you
              </span>
            </div>
          ) : (
            <p className="text-[11px] text-muted-foreground">
              Generated when you prepare the dataset.
            </p>
          )}
        </div>

        <Button
          variant="pink"
          size="lg"
          className="w-full"
          onClick={onStartPrep}
          disabled={!enoughImages || isRunning}
        >
          {isRunning
            ? "Preparing…"
            : isPrepared
              ? "Re-run captioning"
              : "Prepare dataset"}
        </Button>
        {!enoughImages && (
          <p className="text-[11px] text-amber-500">
            Add at least {minImages} reference images first ({imageCount}/
            {minImages}).
          </p>
        )}
        {isPrepared && (
          <p className="text-[11px] text-green-500">
            Dataset prepared — continue to Settings.
          </p>
        )}
        {isFailed && (
          <p className="text-[11px] text-red-500">
            {prepRun?.errorMessage ?? "Dataset prep failed. Try again."}
          </p>
        )}
      </div>

      {/* Right column — progress + review grid */}
      <div className="flex-1 min-w-0">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-xs uppercase tracking-wider text-muted-foreground">
            Captions
          </h3>
          {status && (
            <Badge variant="outline" className="text-[10px]">
              {STATUS_LABEL[status] ?? status}
              {isRunning && prepRun?.imageCount
                ? ` ${prepRun.preparedCount ?? 0}/${prepRun.imageCount}`
                : ""}
            </Badge>
          )}
        </div>

        {loading && captions.length === 0 ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : captions.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No reference images yet. Go back and upload some, then prepare.
          </p>
        ) : (
          <div className="grid grid-cols-2 gap-3 xl:grid-cols-3">
            {captions.map((c) => (
              <PrepCaptionCard
                key={c.assetId}
                caption={c}
                editable={isPrepared}
                onCommit={(text) => onUpdateCaption(c.assetId, text)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

interface PrepCaptionCardProps {
  caption: CaptionWithUrl;
  editable: boolean;
  onCommit: (caption: string) => void;
}

function PrepCaptionCard({
  caption,
  editable,
  onCommit,
}: PrepCaptionCardProps) {
  const [draft, setDraft] = useState(caption.caption ?? "");
  useEffect(() => setDraft(caption.caption ?? ""), [caption.caption]);

  return (
    <div className="overflow-hidden rounded-[8px] border border-white/10 bg-[#1a1a1a]">
      <div className="aspect-square w-full bg-white/5">
        {caption.previewUrl ? (
          <img
            src={caption.previewUrl}
            alt={caption.filePath ?? "reference"}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          <div
            className="h-full w-full"
            style={{ background: "rgba(255, 255, 255, 0.06)" }}
          />
        )}
      </div>
      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          if (editable && draft !== (caption.caption ?? "")) onCommit(draft);
        }}
        disabled={!editable}
        rows={3}
        placeholder={editable ? "Caption…" : "Caption appears after prep"}
        className={cn(
          "w-full resize-none bg-transparent p-2 text-[11px] text-foreground outline-none",
          "placeholder:text-muted-foreground/50",
          !editable && "opacity-60",
        )}
      />
    </div>
  );
}
